import type { LocaleEnum } from '../enums'
import { CSVValue } from './csv-generator.utils/csv-value'

export type Metadata = Record<string, string | number>
export type Columns<Row> = Record<string, GetValueFn<Row>>
type GetValueFn<Row> = (row: Row) => Value
export type Value = number | string | string[] | undefined | null

export class CSVGeneratorUtils {
  private static readonly VALUE_SEPARATOR = ','
  private static readonly NEW_LINE = '\n'

  /**
   * Generates a CSV as a string.
   *
   * @param metadata - Metadata that will be rendered as multiple rows at the beginning of the file.
   * @param columns - Definition of the columns that will be rendered.
   * @param rows - Data for each of the rows.
   * @param locale - Locale to use when formatting values.
   * @returns A string with the CSV.
   */
  static generateCSV<Row>(
    metadata: Metadata,
    columns: Columns<Row>,
    rows: Row[],
    locale: LocaleEnum,
  ): string {
    columns = this.removeEmptyColumns<Row>(columns, rows, locale)

    const metadataRows = this.getMetadataRows(metadata)
    const itemRows = [
      ...(rows.length ? [this.getHeaderRows(columns)] : []),
      ...(rows.length ? [this.getDataRows(columns, rows, locale)] : []),
    ]

    if (metadataRows.length) {
      return [this.getMetadataRows(metadata), '', ...itemRows].join(this.NEW_LINE)
    } else {
      return itemRows.join(this.NEW_LINE)
    }
  }

  private static removeEmptyColumns<Row>(
    columns: Columns<Row>,
    rows: Row[],
    locale: LocaleEnum,
  ): Columns<Row> {
    const nonEmptyColumnNames = new Set()
    rows.forEach((row: Row) => {
      Object.keys(columns).forEach((columnName: keyof Columns<Row>) => {
        const getValueFn = columns[columnName]
        if (
          !nonEmptyColumnNames.has(columnName) &&
          !new CSVValue(getValueFn(row), locale).isEmpty()
        ) {
          nonEmptyColumnNames.add(columnName)
        }
      })
    })

    return Object.keys(columns).reduce(
      (nonEmptyColumns: Columns<Row>, columnName: keyof Columns<Row>) => {
        if (nonEmptyColumnNames.has(columnName)) {
          nonEmptyColumns = {
            ...nonEmptyColumns,
            [columnName]: columns[columnName],
          }
        }
        return nonEmptyColumns
      },
      {},
    )
  }

  private static getMetadataRows(metadata: Metadata): string {
    const metadataRows = Object.keys(metadata).map((metadataKey: string) => {
      return [metadataKey, metadata[metadataKey]].join(this.VALUE_SEPARATOR)
    })

    return metadataRows.join(this.NEW_LINE)
  }

  private static getHeaderRows<Row>(columns: Columns<Row>): string {
    return Object.keys(columns).join(this.VALUE_SEPARATOR)
  }

  private static getDataRows<Row>(columns: Columns<Row>, rows: Row[], locale: LocaleEnum): string {
    return rows
      .map(row => {
        return this.getDataRow(columns, row, locale)
      })
      .join(this.NEW_LINE)
  }

  private static getDataRow<Row>(columns: Columns<Row>, row: Row, locale: LocaleEnum): string {
    return Object.values(columns)
      .map(getValueFn => new CSVValue(getValueFn(row), locale).toString())
      .join(this.VALUE_SEPARATOR)
  }
}
